export type CoachLeaderDetailRequiredSource = "relationship" | "profile";

export type CoachLeaderDetailOptionalSource =
  | "transcripts"
  | "reflections"
  | "artifacts"
  | "practice"
  | "graph"
  | "intelligence"
  | "coachContext"
  | "billing"
  | "unreadMessages"
  | "processingUsage";

export type CoachLeaderDetailRequiredSourceState = "ready" | "retryable_error" | "revoked";
export type CoachLeaderDetailOptionalSourceState =
  | "ready"
  | "empty"
  | "retryable_error"
  | "revoked"
  | "unknown";

export type CoachLeaderDetailSourceReadiness = {
  status: "ready" | "partial" | "retryable_error" | "revoked";
  hardGate: boolean;
  canRetry: boolean;
  sources: Record<CoachLeaderDetailRequiredSource, CoachLeaderDetailRequiredSourceState>
    & Record<CoachLeaderDetailOptionalSource, CoachLeaderDetailOptionalSourceState>;
  unavailableSections: string[];
  emptySections: string[];
};

const SOURCE_LABELS: Record<CoachLeaderDetailRequiredSource | CoachLeaderDetailOptionalSource, string> = {
  relationship: "Coaching relationship",
  profile: "Leader profile",
  transcripts: "Transcripts",
  reflections: "Reflections",
  artifacts: "Artifacts",
  practice: "Practice context",
  graph: "Context graph",
  intelligence: "Behavioral intelligence",
  coachContext: "Coach context",
  billing: "Billing",
  unreadMessages: "Unread messages",
  processingUsage: "Meeting processing usage",
};

export function buildCoachLeaderDetailSourceReadiness(input: {
  required: Record<CoachLeaderDetailRequiredSource, CoachLeaderDetailRequiredSourceState>;
  optional: Record<CoachLeaderDetailOptionalSource, CoachLeaderDetailOptionalSourceState>;
}): CoachLeaderDetailSourceReadiness {
  const sources = { ...input.required, ...input.optional };
  const requiredEntries = Object.entries(input.required) as Array<[
    CoachLeaderDetailRequiredSource,
    CoachLeaderDetailRequiredSourceState,
  ]>;
  const optionalEntries = Object.entries(input.optional) as Array<[
    CoachLeaderDetailOptionalSource,
    CoachLeaderDetailOptionalSourceState,
  ]>;
  const revokedRequired = requiredEntries.find(([, state]) => state === "revoked");
  const retryableRequired = requiredEntries.find(([, state]) => state === "retryable_error");
  const unavailableOptional = optionalEntries.filter(([, state]) => (
    state === "retryable_error" || state === "revoked" || state === "unknown"
  ));

  const unavailableSections = [
    ...(revokedRequired ? [SOURCE_LABELS[revokedRequired[0]]] : []),
    ...(retryableRequired ? [SOURCE_LABELS[retryableRequired[0]]] : []),
    ...unavailableOptional.map(([source]) => SOURCE_LABELS[source]),
  ];

  return {
    status: revokedRequired
      ? "revoked"
      : retryableRequired
        ? "retryable_error"
        : unavailableOptional.length > 0
          ? "partial"
          : "ready",
    hardGate: Boolean(revokedRequired || retryableRequired),
    canRetry: Boolean(retryableRequired || optionalEntries.some(([, state]) => (
      state === "retryable_error" || state === "unknown"
    ))),
    sources,
    unavailableSections,
    emptySections: optionalEntries
      .filter(([, state]) => state === "empty")
      .map(([source]) => SOURCE_LABELS[source]),
  };
}
